import type fr from '../fr/adminOps';

const en: typeof fr = {
  dashboard: {
    title: 'Home',
    openPlanning: 'Open planning',
    todayPlanning: 'Today’s schedule',
    noJobsToday: 'No cleanings scheduled today.',
    issues: 'Needs attention',
    noIssues: 'Nothing to report right now.',
    metrics: 'Key figures',
    mapPreview: 'Today’s cleanings',
    openMap: 'Open map',
  },
  summary: {
    total: 'Cleanings today',
    assigned: 'Assigned',
    toAssign: 'To assign',
    inProgress: 'In progress',
    completedHint_one: '{{count}} completed',
    completedHint_other: '{{count}} completed',
    activeEmployees: 'Active employees',
    expectedRevenue: 'Expected revenue',
    expectedPayroll: 'Expected payroll',
  },
  issues: {
    UNASSIGNED_JOBS_one: '{{count}} cleaning to assign this week',
    UNASSIGNED_JOBS_other: '{{count}} cleanings to assign this week',
    EMPLOYEE_ABSENT_one: '{{count}} cleaning assigned to an absent employee',
    EMPLOYEE_ABSENT_other: '{{count}} cleanings assigned to an absent employee',
    SCHEDULE_CONFLICT_one: '{{count}} schedule conflict',
    SCHEDULE_CONFLICT_other: '{{count}} schedule conflicts',
    QUOTES_TO_APPROVE_one: '{{count}} quote to process',
    QUOTES_TO_APPROVE_other: '{{count}} quotes to process',
    UNREAD_MESSAGES_one: '{{count}} unread message',
    UNREAD_MESSAGES_other: '{{count}} unread messages',
    FAILED_PAYMENTS_one: '{{count}} failed payment',
    FAILED_PAYMENTS_other: '{{count}} failed payments',
    REVIEW_REQUIRED_one: '{{count}} cleaning to review',
    REVIEW_REQUIRED_other: '{{count}} cleanings to review',
  },
  metrics: {
    jobsWeek: 'Cleanings this week',
    jobsToday_one: '{{count}} today',
    jobsToday_other: '{{count}} today',
    revenueWeek: 'Revenue (week)',
    employeeCostWeek: 'Employee cost (week)',
    marginWeek: 'Margin (week)',
    marginPct: '{{pct}}% of revenue',
    quotesToProcess: 'Quotes to process',
    acceptanceRate: 'Acceptance rate',
    acceptanceHint: 'Quotes from the last 90 days',
    recurringClients: 'Recurring clients',
  },
  filters: {
    allStatuses: 'All statuses',
    allSectors: 'All sectors',
    allEmployees: 'All employees',
    allTeams: 'All teams',
  },
  planning: {
    title: 'Planning',
    view: { day: 'Day', week: 'Week', month: 'Month', employee: 'Employee', team: 'Team' },
    jobsCount_one: '{{count}} cleaning',
    jobsCount_other: '{{count}} cleanings',
    more_one: '+{{count}} more',
    more_other: '+{{count}} more',
    free: 'Free',
    emptyDay: 'No cleanings that day.',
    unassignedRow: 'Unassigned',
    noTeamRow: 'No team',
    moved: 'Cleaning moved. The client and crew have been notified.',
    dragHint: 'Drag a cleaning to move it.',
    dragHintEmployee: 'Drag a cleaning to another row to change the employee.',
  },
  dispatch: {
    button: 'Today’s dispatch',
    title: 'Dispatch for {{date}}',
    description: 'Best proposal for each unassigned cleaning. The engine proposes, you decide.',
    nothing: 'Every cleaning that day is assigned.',
    confirm: 'Confirm',
    confirmed: 'Assignment confirmed.',
    review: 'See options',
    noProposal: 'No possible proposal.',
  },
  newJob: {
    button: 'New cleaning',
    title: 'New cleaning',
    client: 'Client',
    clientSearch: 'Name, email or phone…',
    noClient: 'No client found.',
    changeClient: 'Change',
    property: 'Property',
    noProperty: 'This client has no property. Add one from their profile.',
    service: 'Service',
    priceHint: 'Price agreed with the client.',
    invalid: 'Invalid value.',
    submit: 'Create cleaning',
    created: 'Cleaning created.',
  },
  job: {
    title: 'Cleaning #{{number}}',
    loading: 'Cleaning',
    notFound: 'Cleaning not found',
    open: 'Open details',
    when: 'Date and time',
    actual: 'Actual duration: {{duration}}',
    address: 'Address',
    crewNeeds: 'Needs',
    workers_one: '{{count}} person',
    workers_other: '{{count}} people',
    priority: '{{p}} priority',
    sector: 'Sector {{name}}',
    property: 'Property',
    rooms: '{{bedrooms}} bd · {{bathrooms}} ba · {{floors}} floor(s)',
    pets: 'Pets {{details}}',
    parking: 'Parking: {{info}}',
    cutoff: 'Client cutoff',
    cutoffLocked: 'Cutoff passed: any date change is an override.',
    cutoffOpen: 'The client can make changes until {{deadline}}',
    permanentNotes: 'Permanent property notes',
    completionNotes: 'Completion notes',
    cancelReason: 'Cancellation reason',
    price: 'Price',
  },
  crew: {
    title: 'Assigned crew',
    none: 'Unassigned',
    noneLong_one: 'No employee assigned ({{count}} required).',
    noneLong_other: 'No employee assigned ({{count}} required).',
    missing_one: '{{count}} person missing.',
    missing_other: '{{count}} people missing.',
    lead: 'Lead',
    team: 'Team: {{name}}',
  },
  money: {
    title: 'Profitability',
    price: 'Client price',
    crewCost: 'Crew cost',
    margin: 'Margin',
  },
  compensation: {
    adjust: 'Adjust pay',
    amount: 'Amount ($)',
    auto: 'Automatic',
    saved: 'Pay updated.',
  },
  assign: {
    title: 'Assign cleaning',
    dispatchTitle: 'Dispatch',
    suggestions: 'Suggestions',
    manual: 'Manual pick',
    engineHint: 'Engine ranking (lowest score is best). The engine proposes, you decide.',
    suggestionsError: 'Suggestions could not be computed.',
    noCandidates: 'No employee available.',
    assign: 'Assign',
    reassign: 'Reassign',
    assignSelected: 'Assign selection',
    done: 'Assignment saved.',
    notAssignable: 'This cleaning can no longer be assigned.',
    team: 'Team',
    teamHint: 'Choosing a team selects its active members.',
    noTeam: 'No team',
    employees: 'Employees',
    selectedCount: '{{selected}} of {{required}} required',
    onLeave: 'On leave',
    todayJobs_one: '{{count}} cleaning today',
    todayJobs_other: '{{count}} cleanings today',
    conflictTitle: 'This assignment creates a conflict',
    notEnough: '{{selected}} employee(s) selected for {{required}} required.',
    forceReason: 'Override reason',
    force: 'Force assignment',
    forceNotAllowed: 'Only an administrator can force this assignment.',
    unassign: 'Unassign',
    unassigned: 'Cleaning unassigned.',
    unassignTitle: 'Remove the crew from this cleaning?',
    unassignDescription: '{{client}}’s cleaning will go back to the list to assign. The employees will be notified.',
  },
  candidate: {
    eligible: 'Eligible',
    ineligible: 'Conflict',
    kind: { EMPLOYEE: 'Employee', TEAM: 'Team', PAIR: 'Pair' },
    score: 'Score {{score}}',
    none: 'No possible combination',
    far: 'Long distance',
    showBreakdown: 'Show score details',
    hideBreakdown: 'Hide details',
  },
  score: {
    distance: 'Travel',
    distanceValue: '{{km}} km · {{min}} min',
    distanceCost: 'Travel cost',
    continuityBonus: 'Continuity bonus',
    sectorBonus: 'Sector bonus',
    capacityPenalty: 'Day load',
    overtimePenalty: 'Overtime',
    schedulePenalty: 'Tight schedule',
  },
  conflict: {
    INACTIVE: 'Employee inactive or on leave',
    TIME_OFF: 'Time off that day',
    OUTSIDE_AVAILABILITY: 'Outside their availability',
    OVERLAP: 'Overlaps another cleaning',
    MAX_JOBS: 'Maximum number of cleanings reached',
    MAX_HOURS: 'Maximum hours exceeded',
    MISSING_SKILL: 'Required skill missing',
    SECTOR_NOT_ALLOWED: 'Sector not served by this employee',
    TEAM_CAPACITY: 'Team capacity reached',
    NOT_ENOUGH_WORKERS: 'Not enough available employees',
  },
  status: {
    title: 'Change status',
    quick: 'Status',
    changed: 'Status updated.',
    reasonTitle: 'Move to “{{status}}”',
    overrideTitle: 'Complete despite the incomplete checklist',
    overrideChecklist: 'Some required tasks are not checked. Explain why the cleaning is complete anyway.',
    overrideConfirm: 'Complete',
    action: {
      DRAFT: 'Draft',
      UNASSIGNED: 'Back to unassigned',
      ASSIGNED: 'Mark assigned',
      CONFIRMED: 'Confirm',
      EN_ROUTE: 'On the way',
      ARRIVED: 'Arrived',
      IN_PROGRESS: 'Start',
      COMPLETED: 'Complete',
      REVIEW_REQUIRED: 'Request review',
      CANCELLED: 'Cancel',
      NO_SHOW: 'Client no-show',
    },
  },
  override: {
    cutoffTitle: 'Client cutoff passed',
    cutoffDescription: 'This cleaning is inside the lock period. Give the reason for the change (it will be logged).',
    confirm: 'Change anyway',
  },
  reason: {
    label: 'Reason',
    hint: 'At least 2 characters.',
  },
  edit: {
    title: 'Edit cleaning #{{number}}',
    saved: 'Cleaning updated.',
    durationMinutes: 'Duration (minutes)',
    price: 'Client price ($)',
    workers: 'Number of people',
    priority: 'Priority',
    notes: 'Internal notes',
    notesHint: 'Visible to staff only.',
    specialInstructions: 'Special instructions',
    instructionsHint: 'Visible to the crew and the client.',
    lockedHint: 'Client cutoff passed: changing the date or time will require a reason.',
    lockedNoOverride: 'Client cutoff passed: only an administrator can change the date or time.',
  },
  cancel: {
    button: 'Cancel cleaning',
    title: 'Cancel cleaning #{{number}}?',
    description: 'The client and crew will be notified.',
    lockedDescription: 'The client cutoff has passed: this cancellation is a logged override.',
    reason: 'Cancellation reason',
    confirm: 'Cancel cleaning',
    keep: 'Keep',
    done: 'Cleaning cancelled.',
    notifyHint: 'The reason is kept in the history.',
  },
  checklist: {
    title: 'Checklist',
    progress: '{{done}} / {{total}}',
    empty: 'No tasks for this cleaning.',
    optional: 'optional',
    addedBadge: 'Added',
    section: 'Section',
    newTask: 'New task',
    added: 'Task added.',
    removed: 'Task removed.',
    remove: 'Remove “{{label}}”',
    removeTitle: 'Remove this task?',
  },
  history: {
    title: 'History',
    empty: 'No status changes.',
    created: 'Created: {{status}}',
    override: 'Override',
    system: 'System',
  },
  map: {
    title: 'Map',
    description: 'Properties, today’s cleanings, employee homes and sectors.',
    filter: 'Filter',
    mode: { all: 'All', unassigned: 'Unassigned', team: 'Team', employee: 'Employee', sector: 'Sector', status: 'Status' },
    chooseValue: 'Choose…',
    layer: { houses: 'Houses', homes: 'Homes', sectors: 'Sectors' },
    homeOf: '{{name}}’s home',
    jobsTab_one: '{{count}} cleaning',
    jobsTab_other: '{{count}} cleanings',
    noJobs: 'No cleanings for this filter.',
    noCoordinates: 'address not located',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    resetView: 'Recenter',
  },
  route: {
    title: 'Day route',
    employee: 'Employee',
    chooseEmployee: 'Choose an employee…',
    noJobs: 'no cleanings',
    hint: 'Choose an employee to see their route for the day.',
    show: 'Show route',
    optimize: 'Optimize',
    optimized: 'Optimized',
    optimizeHint: 'Optimization may shift arrivals by up to 45 minutes; promised times are not changed.',
    empty: 'No stops that day.',
    feasible: 'Feasible',
    notFeasible: 'Delays expected',
    totals: '{{km}} km · {{min}} min driving',
    leg: '{{min}} min · {{km}} km · arrival {{arrival}}',
    wait: 'wait {{min}} min',
    late: '{{min}} min late',
    noCoordinates_one: '{{count}} stop without coordinates.',
    noCoordinates_other: '{{count}} stops without coordinates.',
  },
};
export default en;
